import { ThemeProvider, createTheme } from '@mui/material'
import { Layout } from 'components/Layout/Layout'
import { useUser } from 'hooks/user'
import Dashboard from 'pages/Dashboard/Dashboard'
import DashboardNormalized from 'pages/DashboardNormalized/DashboardNormalized'
import Facility from 'pages/Facility/Facility'
import FacilityList from 'pages/FacilityList/FacilityList'
import MapList from 'pages/MapList/MapList'
import Mission from 'pages/Mission/Mission'
import MissionControl from 'pages/MissionControl/MissionControl'
import MissionList from 'pages/MissionList/MissionList'
import NotFound from 'pages/NotFound/NotFound'
import RecordedRouteList from 'pages/RecordedRouteList/RecordedRouteList'
import Robot from 'pages/Robot/Robot'
import RobotList from 'pages/RobotList/RobotList'
import Scenario from 'pages/Scenario/Scenario'
import ScenarioList from 'pages/ScenarioList/ScenarioList'
import NewPassword from 'pages/Signin/NewPassword/NewPassword'
import RestorePassword from 'pages/Signin/RestorePassword/RestorePassword'
import Signin from 'pages/Signin/Signin'
import Station from 'pages/Station/Station'
import StationList from 'pages/StationList/StationList'
import Scheduler from 'pages/TaskScheduler/TaskScheduler'
import TaskSchedulerNormalized from 'pages/TaskSchedulerNormalized/TaskSchedulerNormalized'
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { PATH } from './utils/constants'

const theme = createTheme({
  palette: {
    primary: {
      main: '#2E70EF',
    },
    secondary: {
      main: '#000A19CC',
    },
  },
})

function PrivatePage({ children }: { children: JSX.Element }) {
  const user = useUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (!user?.state.userLoggedIn) {
      navigate(PATH.SIGNIN)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.state.userLoggedIn])

  return children
}

function AuthPage({ children }: { children: JSX.Element }) {
  const location = useLocation()
  const user = useUser()

  if (user?.state.userLoggedIn) {
    return <Navigate to={PATH.ASSET_LIST} state={{ from: location }} />
  }

  return children
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout.PublicPage />}>
            <Route path={PATH.SIGNIN} element={<AuthPage children={<Signin />} />} />
            <Route path={PATH.RESTORE_PASSWORD} element={<AuthPage children={<RestorePassword />} />} />
            <Route path={PATH.NEW_PASSWORD} element={<AuthPage children={<NewPassword />} />} />
            <Route path={PATH.NOT_FOUND} element={<NotFound />} />
          </Route>
          <Route element={<Layout.MainPage />}>
            <Route path={PATH.MISSION_LIST} element={<PrivatePage children={<MissionList />} />} />
            <Route path={PATH.FACILITY_LIST} element={<PrivatePage children={<FacilityList />} />} />
            <Route path={PATH.DASHBOARD} element={<PrivatePage children={<DashboardNormalized />} />} />
            <Route path={PATH.DASHBOARD_LEGACY} element={<PrivatePage children={<Dashboard />} />} />
            <Route path={PATH.DASHBOARD_NORMALIZED} element={<Navigate to={PATH.DASHBOARD} replace />} />
            <Route path={PATH.MISSION_ID} element={<PrivatePage children={<Mission />} />} />
            <Route path={PATH.ASSET_LIST} element={<PrivatePage children={<RobotList />} />} />
            <Route path={PATH.STATION_LIST} element={<PrivatePage children={<StationList />} />} />
            <Route path={PATH.RECORDED_ROUTE_LIST} element={<PrivatePage children={<RecordedRouteList />} />} />
            <Route path={PATH.ROBOT_ID} element={<PrivatePage children={<Robot />} />} />
            <Route path={PATH.STATION_ID} element={<PrivatePage children={<Station />} />} />
            <Route path={PATH.MISSION_CONTROL} element={<PrivatePage children={<MissionControl />} />} />
            <Route path={PATH.FACILITY_ID} element={<PrivatePage children={<Facility />} />} />
            <Route path={PATH.SCENARIO_ID} element={<PrivatePage children={<Scenario />} />} />
            <Route path={PATH.SCENARIO_LIST} element={<PrivatePage children={<ScenarioList />} />} />
            <Route path={PATH.MAP_LIST} element={<PrivatePage children={<MapList />} />} />
            <Route path={PATH.TASKS} element={<PrivatePage children={<Scheduler />} />} />
            <Route path={PATH.TASKS_NORMALIZED} element={<PrivatePage children={<TaskSchedulerNormalized />} />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
